import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RATE_PATTERN, type FxRate } from '@miraki/contracts';
import { api, ApiError } from '../api/client';
import { useCountries } from '../api/hooks';
import { useSession } from '../auth/session';
import { Alert, Button, Chip, Field, Note, PageHeader, Panel, Spinner, Table } from '../ui';

/**
 * A-44 Exchange rates — CR-55 / D72, D103. The rate a portal quotes its retailers, as at a date.
 *
 * INTERNAL screen, so its strings are literals and not `t()`: `packages/i18n` covers the
 * customer-facing half only.
 *
 * The screen is a list of COUNTRIES, but a rate is stored per CURRENCY PAIR. Two countries that
 * read the same currency are behind one row, so they show the same rate; the "Shared with" column
 * says so instead of letting it look like two settings. A rate is a fact as at a date: there is no
 * edit and no delete, by design (see the note on the screen).
 *
 * Reading needs `portal.view`; adding needs `system.config`. A rate is a decimal STRING end to
 * end — it is displayed and sent as typed, never parsed to a number (NF-04).
 */

interface PortalView { facts: { currency: string } }

const KEY = ['fx-rates'];

/** `1.50000000` reads better as `1.5`; trimmed on the text, never through a number. */
const tidy = (rate: string): string => rate.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');

/** Newest row whose effective date is not after `at`. A post-dated rate is not in force yet. */
function inForce(rows: readonly FxRate[], currency: string, at: number): FxRate | null {
  let best: FxRate | null = null;
  for (const r of rows) {
    if (r.quoteCurrency !== currency) continue;
    const t = Date.parse(r.effectiveFrom);
    if (t <= at && (best === null || t > Date.parse(best.effectiveFrom))) best = r;
  }
  return best;
}

const when = (iso: string): string => new Date(iso).toLocaleString();

/** `datetime-local` wants local wall-clock time without a zone. */
function nowLocal(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export function ExchangeRates() {
  const { can } = useSession();
  const qc = useQueryClient();
  const mayAdd = can('system.config');

  const rates = useQuery({ queryKey: KEY, queryFn: () => api.get<FxRate[]>('/fx-rates') });
  const portal = useQuery({ queryKey: ['portal-settings'], queryFn: () => api.get<PortalView>('/portal-settings') });
  const countries = useCountries();

  const [currency, setCurrency] = useState('');
  const [rate, setRate] = useState('');
  const [effective, setEffective] = useState(nowLocal);
  const [source, setSource] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (body: { quoteCurrency: string; rate: string; effectiveFrom: string; source?: string }) =>
      api.post<FxRate>('/fx-rates', body),
    onSuccess: () => {
      setRate(''); setSource(''); setEffective(nowLocal()); setFailure(null);
      void qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (e) => setFailure(e instanceof ApiError ? e.message : 'The rate could not be saved.'),
  });

  const base = portal.data?.facts.currency ?? null;
  const rows = rates.data ?? [];
  const now = Date.now();

  /** Countries by the currency they read — the thing a rate is actually stored against. */
  const byCurrency = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const c of countries.data ?? []) m.set(c.defaultCurrency, [...(m.get(c.defaultCurrency) ?? []), c.name]);
    return m;
  }, [countries.data]);

  if (rates.isLoading || portal.isLoading || countries.isLoading) return <Spinner />;
  if (rates.error) return <Alert tone="stop">{rates.error.message}</Alert>;
  if (portal.error) return <Alert tone="stop">{portal.error.message}</Alert>;
  if (countries.error) return <Alert tone="stop">{countries.error.message}</Alert>;

  const quotable = (countries.data ?? []).filter((c) => c.defaultCurrency !== base);
  const sharing = currency ? (byCurrency.get(currency) ?? []) : [];

  const rateOk = RATE_PATTERN.test(rate) && /[1-9]/.test(rate);
  const canSubmit = currency !== '' && rateOk && effective !== '' && !add.isPending;

  const submit = () => {
    setFailure(null);
    add.mutate({
      quoteCurrency: currency, rate, effectiveFrom: new Date(effective).toISOString(),
      ...(source.trim() ? { source: source.trim() } : {}),
    });
  };

  const status = (r: FxRate): { tone: 'done' | 'us' | 'lapse'; text: string } => {
    if (Date.parse(r.effectiveFrom) > now) return { tone: 'us', text: 'Scheduled' };
    return inForce(rows, r.quoteCurrency, now)?.id === r.id
      ? { tone: 'done', text: 'In force' }
      : { tone: 'lapse', text: 'Superseded' };
  };

  return (
    <>
      <PageHeader
        title="Exchange rates"
        sub={`What retailers on this portal are shown beside the ${base ?? 'base'} price. Prices are always held in ${base ?? 'the base currency'}; a rate only adds an indicative figure.`}
      />

      <Panel title="Countries and the rate they read" pad>
        <Table
          rowKey={(c) => c.code}
          rows={countries.data ?? []}
          empty="No countries are configured."
          columns={[
            { header: 'Country', render: (c) => c.name },
            { header: 'Currency read', render: (c) => c.defaultCurrency },
            {
              header: 'Rate in force',
              render: (c) => {
                if (c.defaultCurrency === base) return <span className="mk-dim">Portal currency — no rate needed</span>;
                const r = inForce(rows, c.defaultCurrency, now);
                return r
                  ? <>1 {r.baseCurrency} = <b>{tidy(r.rate)}</b> {r.quoteCurrency} <span className="mk-dim">as at {when(r.effectiveFrom)}</span></>
                  : <span className="mk-dim">None yet — retailers see the {base} price alone</span>;
              },
            },
            {
              header: 'Shared with',
              render: (c) => {
                const others = (byCurrency.get(c.defaultCurrency) ?? []).filter((n) => n !== c.name);
                return others.length === 0 || c.defaultCurrency === base
                  ? <span className="mk-dim">—</span>
                  : <Chip tone="them" title="One rate is stored per currency, so these countries read the same row">{others.join(', ')}</Chip>;
              },
            },
          ]}
        />
        <Note>
          A rate is stored per currency, not per country. Countries that read the same currency are
          behind one row, so changing it for one changes it for all of them.
        </Note>
      </Panel>

      <Panel title="Rate history" pad>
        <Table
          rowKey={(r) => r.id}
          rows={rows}
          empty="No rate has been entered yet."
          columns={[
            { header: 'Pair', render: (r) => `${r.baseCurrency} → ${r.quoteCurrency}` },
            { header: 'Rate', numeric: true, render: (r) => tidy(r.rate) },
            { header: 'Effective from', render: (r) => when(r.effectiveFrom) },
            { header: 'Status', render: (r) => { const s = status(r); return <Chip tone={s.tone}>{s.text}</Chip>; } },
            { header: 'Source', render: (r) => r.source ?? <span className="mk-dim">—</span> },
            { header: 'Entered by', render: (r) => r.enteredByEmail ?? <span className="mk-dim">—</span> },
          ]}
        />
        <Note>
          There is no edit or delete: a rate is a fact as at a date, so correcting one means adding
          today's. The new rate takes over from its effective date and the old row stays as history.
        </Note>
      </Panel>

      {mayAdd ? (
        <Panel title="Add a rate" pad>
          <div className="mk-fset">
            <Field label="Country" hint="The country fixes the currency the rate is stored against.">
              <select className="mk-fld" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                <option value="">Choose a country…</option>
                {quotable.map((c) => (
                  <option key={c.code} value={c.defaultCurrency}>{c.name} — {c.defaultCurrency}</option>
                ))}
              </select>
            </Field>
            {sharing.length > 1 ? (
              <Alert tone="us">This rate applies to every country that reads {currency}: {sharing.join(', ')}.</Alert>
            ) : null}
            <Field
              label={`Rate${currency && base ? ` (1 ${base} = ? ${currency})` : ''}`}
              hint="Up to 8 decimal places. Typed exactly as you want it stored."
              error={rate !== '' && !rateOk ? 'A positive number with at most 8 decimal places' : null}
            >
              <input className="mk-fld" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value.trim())} placeholder="3.6725" />
            </Field>
            <Field label="Effective from" hint="In your browser's time zone. A future date schedules the rate.">
              <input className="mk-fld" type="datetime-local" value={effective} onChange={(e) => setEffective(e.target.value)} />
            </Field>
            <Field label="Source" hint="Optional. Where you got it — free text.">
              <input className="mk-fld" value={source} maxLength={500} onChange={(e) => setSource(e.target.value)} />
            </Field>
          </div>
          {failure ? <Alert tone="stop">{failure}</Alert> : null}
          <div style={{ marginTop: 'var(--mk-space-3)' }}>
            <Button disabled={!canSubmit} onClick={submit}>{add.isPending ? 'Saving…' : 'Add rate'}</Button>
          </div>
        </Panel>
      ) : (
        <Note>You can read the rates but not add one — that needs the system configuration permission.</Note>
      )}
    </>
  );
}
